// Translations for Piastre.one - French & English

export const translations = {
  en: {
    // History Page
    history: {
      title: "History of the Piastre",
      subtitle: "A Journey Through Quebec's Monetary Heritage",
      intro: "The word 'piastre' (pronounced 'piasse' in Quebec French) is deeply rooted in the history of New France and remains a beloved term for 'dollar' in Quebec to this day. This is the story of how a Spanish silver coin shaped Canadian monetary culture.",
      
      sections: {
        origins: {
          title: "Spanish Origins",
          period: "16th - 17th Century",
          content: "The piastre traces its origins to the Spanish 'piece of eight' (real de a ocho), a silver coin minted in Spain and its American colonies—Mexico and Peru—starting in the 16th century. The term 'piastre' itself comes from the Italian 'piastra', meaning 'thin metal plate', used by Venetian traders.",
          highlight: "The Spanish dollar was the world's first global currency, accepted from Europe to China."
        },
        newFrance: {
          title: "New France Era",
          period: "1600s - 1760",
          content: "Due to a chronic shortage of French coins in the colony, Spanish piastres became the primary hard currency in New France. Intendant Jean Talon mentioned them in his correspondence as early as 1680. These silver coins, mined in Mexico and Peru, flowed into Quebec through trade with English and Dutch settlers.",
          highlight: "1 piastre = 6 livres = 120 sous (French colonial currency)"
        },
        cardMoney: {
          title: "Card Money Innovation",
          period: "1685 - 1760",
          content: "Facing coin shortages, New France created an ingenious solution: card money. Playing cards were cut, stamped with values, and signed by colonial officials. A full card was worth 4 livres. This was one of North America's first paper currencies, though overissuance eventually eroded public trust.",
          highlight: "Playing cards became official currency—a uniquely Canadian monetary innovation."
        },
        transition: {
          title: "British Conquest & Transition",
          period: "1760 - 1858",
          content: "After the British conquest of 1760, the French livre paper currency collapsed. The Spanish dollar continued circulating alongside British pounds. The Quebec Gazette (1766) used 'piastre' to translate 'dollar'. The term became synonymous with the monetary unit in French Canadian speech.",
          highlight: "The expression 'changer quatre trente-sous pour une piastre' (exchanging four 30-cent pieces for a dollar) dates from this era."
        },
        modern: {
          title: "The Canadian Dollar",
          period: "1858 - Present",
          content: "In 1858, Canada officially adopted the decimal dollar as its national currency, replacing the pound. While 'dollar' became the official English term, French Canadians kept using 'piastre' in everyday speech. The word appeared on Canadian banknotes until the mid-20th century.",
          highlight: "'Piasse' remains the most common Quebec slang for dollar today."
        }
      },
      
      timeline: {
        title: "Key Dates",
        events: [
          { year: "1535", event: "First Spanish pesos minted in Mexico City" },
          { year: "1608", event: "Founding of Quebec City by Samuel de Champlain" },
          { year: "1680", event: "Intendant Talon documents piastre circulation" },
          { year: "1685", event: "First card money issued in New France" },
          { year: "1760", event: "British conquest; livre paper currency collapses" },
          { year: "1858", event: "Canada adopts the decimal dollar" },
          { year: "1935", event: "Bank of Canada established" }
        ]
      },
      
      funFacts: {
        title: "Did You Know?",
        facts: [
          "The '$' symbol may originate from the Spanish peso, derived from 'PS' (pesos) written as an overlapping monogram.",
          "A 'trente-sous' (30 cents) still refers to a quarter (25¢) in Quebec—a relic of old exchange rates.",
          "Archaeological digs in Quebec City have uncovered Spanish coins from 1736 (Seville) and 1810 (Madrid).",
          "The pirate 'pieces of eight' in Treasure Island are the same coins that circulated in Quebec."
        ]
      },
      
      quote: {
        text: "The piastre is not just a word—it's a living piece of our heritage, connecting modern Quebecers to the merchants, coureurs des bois, and settlers who built New France.",
        author: "Quebec Monetary History"
      },
      
      sources: {
        title: "Sources & References",
        academic: "Academic Sources",
        museums: "Museums & Archives",
        list: [
          { name: "Bank of Canada Museum", url: "https://www.bankofcanadamuseum.ca", desc: "Comprehensive collection of Canadian monetary history" },
          { name: "Wikipedia - Piastre", url: "https://fr.wikipedia.org/wiki/Piastre", desc: "Etymology and historical usage" },
          { name: "Wikipedia - Piece of Eight", url: "https://en.wikipedia.org/wiki/Spanish_dollar", desc: "History of the Spanish dollar globally" },
          { name: "Patrimoine Culturel du Québec", url: "https://www.patrimoine-culturel.gouv.qc.ca", desc: "Archaeological findings of Spanish coins in Quebec" },
          { name: "Bank of Canada - Currency 1600-1770", url: "https://www.bankofcanada.ca/wp-content/uploads/2010/07/1600-1770.pdf", desc: "Official monetary history documentation" },
          { name: "Érudit - Histoire Monétaire", url: "https://www.erudit.org", desc: "Academic papers on Quebec financial history" }
        ]
      },
      
      cta: {
        title: "From Piastre to Blockchain",
        description: "Just as the piastre revolutionized trade in New France, Piastre.one brings the next evolution of money to Quebec and beyond.",
        button: "Discover Piastre.one"
      }
    },
    // Navbar
    nav: {
      features: "Features",
      howItWorks: "How It Works",
      security: "Security",
      reviews: "Reviews",
      login: "Log In",
      getAccount: "Get Free Account"
    },
    
    // Hero Section
    hero: {
      badge: "Powered by Stellar Blockchain",
      title1: "Banking for the",
      title2: "decentralized",
      title3: "world.",
      description: "Seamlessly connect your WordPress account to a Stellar wallet. Earn crypto from MyCred points, spend anywhere, and take control of your financial future.",
      ctaPrimary: "Open Free Account",
      ctaSecondary: "See How It Works",
      trustSecurity: "Bank-level security",
      trustInstant: "Instant setup"
    },
    
    // Social Proof
    socialProof: {
      title: "Trusted by leading platforms"
    },
    
    // Features Section
    features: {
      title1: "Everything you need,",
      title2: "all in one place",
      description: "From earning points to spending crypto — manage your entire financial journey with Piastre.one.",
      
      wallet: {
        badge: "Your Wallet",
        title: "Your crypto wallet, automatically created",
        description: "Connect your WordPress account and get a Stellar wallet instantly. No seed phrases to manage — just simple, secure access."
      },
      earn: {
        badge: "Earn Points",
        title: "Turn points into crypto",
        description: "Convert your MyCred points to Stellar tokens with one click."
      },
      spend: {
        badge: "Spend",
        title: "Spend anywhere you go",
        description: "Use your Piastre card for contactless payments worldwide."
      },
      send: {
        badge: "Transfer",
        title: "Send & receive instantly",
        description: "Transfer crypto to friends in seconds with near-zero fees on the Stellar network.",
        youSend: "You send",
        theyReceive: "They receive"
      }
    },
    
    // How It Works
    howItWorks: {
      overline: "Simple Onboarding",
      title: "Get started in 3 easy steps",
      step1: {
        title: "Connect WordPress",
        description: "Link your MyCred WordPress account in seconds with our secure OAuth integration."
      },
      step2: {
        title: "Verify Identity",
        description: "Quick verification to keep your account secure. Takes less than 2 minutes."
      },
      step3: {
        title: "Start Earning",
        description: "Your wallet is ready! Earn tokens, convert points, and spend anywhere."
      }
    },
    
    // Security Section
    security: {
      title: "Your security is our priority",
      description: "Built with the same security standards as traditional banks, enhanced with blockchain transparency.",
      feature1: {
        title: "256-bit Encryption",
        description: "Bank-level security"
      },
      feature2: {
        title: "Fraud Protection",
        description: "24/7 monitoring"
      },
      feature3: {
        title: "Secure Storage",
        description: "Cold wallet backup"
      }
    },
    
    // Stats
    stats: {
      users: "Active Users",
      converted: "Tokens Converted",
      rating: "App Store Rating",
      speed: "Transaction Speed"
    },
    
    // Testimonials
    testimonials: {
      overline: "Loved by thousands",
      title: "What our users say",
      reviews: [
        {
          name: "Sarah Chen",
          role: "WordPress Creator",
          content: "Finally, a crypto wallet that doesn't feel like I need a PhD to use. Connected my MyCred account in seconds!"
        },
        {
          name: "Marcus Johnson",
          role: "Online Entrepreneur",
          content: "The conversion from points to tokens is seamless. My community loves earning real crypto rewards."
        },
        {
          name: "Emily Rodriguez",
          role: "Content Creator",
          content: "Bank-level security with the simplicity of Venmo. This is how crypto should have been from the start."
        },
        {
          name: "David Kim",
          role: "Small Business Owner",
          content: "Integrated with my WordPress store in minutes. Now my customers earn tokens with every purchase!"
        }
      ]
    },
    
    // CTA Section
    cta: {
      title: "Ready to take control of your crypto?",
      description: "Join thousands of WordPress users who've already made the switch to Piastre.one. It's free to start.",
      primaryBtn: "Open Free Account",
      secondaryBtn: "Talk to Sales"
    },
    
    // Footer
    footer: {
      description: "Banking for the decentralized world. Connect, earn, and spend — all from your WordPress account.",
      product: "Product",
      company: "Company",
      legal: "Legal",
      features: "Features",
      pricing: "Pricing",
      security: "Security",
      api: "API",
      about: "About",
      blog: "Blog",
      careers: "Careers",
      contact: "Contact",
      privacy: "Privacy",
      terms: "Terms",
      cookies: "Cookies",
      copyright: "© 2026 Piastre.one. All rights reserved.",
      bankGrade: "Bank-grade Security",
      poweredBy: "Powered by Stellar"
    }
  },
  
  fr: {
    // History Page
    history: {
      title: "Histoire de la Piastre",
      subtitle: "Un voyage à travers le patrimoine monétaire du Québec",
      intro: "Le mot « piastre » (prononcé « piasse » au Québec) est profondément enraciné dans l'histoire de la Nouvelle-France et demeure un terme affectueux pour « dollar » au Québec. Voici l'histoire de comment une pièce d'argent espagnole a façonné la culture monétaire canadienne.",
      
      sections: {
        origins: {
          title: "Origines Espagnoles",
          period: "XVIe - XVIIe siècle",
          content: "La piastre tire ses origines de la « pièce de huit » espagnole (real de a ocho), une pièce d'argent frappée en Espagne et dans ses colonies américaines — le Mexique et le Pérou — dès le XVIe siècle. Le terme « piastre » vient de l'italien « piastra », signifiant « plaque de métal mince », utilisé par les marchands vénitiens.",
          highlight: "Le dollar espagnol fut la première monnaie mondiale, acceptée de l'Europe jusqu'en Chine."
        },
        newFrance: {
          title: "L'Époque de la Nouvelle-France",
          period: "1600 - 1760",
          content: "En raison d'une pénurie chronique de pièces françaises dans la colonie, les piastres espagnoles sont devenues la principale monnaie en Nouvelle-France. L'intendant Jean Talon les mentionne dans sa correspondance dès 1680. Ces pièces d'argent, extraites au Mexique et au Pérou, arrivaient au Québec par le commerce avec les colons anglais et hollandais.",
          highlight: "1 piastre = 6 livres = 120 sous (monnaie coloniale française)"
        },
        cardMoney: {
          title: "Innovation de la Monnaie de Carte",
          period: "1685 - 1760",
          content: "Face aux pénuries de pièces, la Nouvelle-France créa une solution ingénieuse : la monnaie de carte. Des cartes à jouer étaient découpées, estampillées avec des valeurs et signées par les officiels coloniaux. Une carte entière valait 4 livres. Ce fut l'une des premières monnaies de papier en Amérique du Nord, bien que la surémission ait finalement érodé la confiance publique.",
          highlight: "Les cartes à jouer sont devenues monnaie officielle — une innovation monétaire typiquement canadienne."
        },
        transition: {
          title: "Conquête Britannique & Transition",
          period: "1760 - 1858",
          content: "Après la conquête britannique de 1760, la monnaie de papier en livres françaises s'effondre. Le dollar espagnol continue de circuler aux côtés des livres sterling britanniques. La Gazette de Québec (1766) utilise « piastre » pour traduire « dollar ». Le terme devient synonyme de l'unité monétaire dans le parler canadien-français.",
          highlight: "L'expression « changer quatre trente-sous pour une piastre » date de cette époque."
        },
        modern: {
          title: "Le Dollar Canadien",
          period: "1858 - Aujourd'hui",
          content: "En 1858, le Canada adopte officiellement le dollar décimal comme monnaie nationale, remplaçant la livre. Tandis que « dollar » devient le terme officiel en anglais, les Canadiens français continuent d'utiliser « piastre » dans le langage quotidien. Le mot apparaissait sur les billets de banque canadiens jusqu'au milieu du XXe siècle.",
          highlight: "« Piasse » demeure l'argot québécois le plus courant pour dollar aujourd'hui."
        }
      },
      
      timeline: {
        title: "Dates Clés",
        events: [
          { year: "1535", event: "Premiers pesos espagnols frappés à Mexico" },
          { year: "1608", event: "Fondation de Québec par Samuel de Champlain" },
          { year: "1680", event: "L'intendant Talon documente la circulation des piastres" },
          { year: "1685", event: "Première monnaie de carte émise en Nouvelle-France" },
          { year: "1760", event: "Conquête britannique; effondrement de la monnaie de papier" },
          { year: "1858", event: "Le Canada adopte le dollar décimal" },
          { year: "1935", event: "Création de la Banque du Canada" }
        ]
      },
      
      funFacts: {
        title: "Le Saviez-Vous ?",
        facts: [
          "Le symbole « $ » pourrait provenir du peso espagnol, dérivé de « PS » (pesos) écrit comme un monogramme superposé.",
          "Un « trente-sous » (30 cents) désigne toujours un 25¢ au Québec — un vestige des anciens taux de change.",
          "Des fouilles archéologiques à Québec ont mis au jour des pièces espagnoles de 1736 (Séville) et 1810 (Madrid).",
          "Les « pièces de huit » des pirates dans L'Île au trésor sont les mêmes pièces qui circulaient au Québec."
        ]
      },
      
      quote: {
        text: "La piastre n'est pas qu'un mot — c'est un morceau vivant de notre patrimoine, reliant les Québécois d'aujourd'hui aux marchands, coureurs des bois et colons qui ont bâti la Nouvelle-France.",
        author: "Histoire Monétaire du Québec"
      },
      
      sources: {
        title: "Sources & Références",
        academic: "Sources Académiques",
        museums: "Musées & Archives",
        list: [
          { name: "Musée de la Banque du Canada", url: "https://www.museedelabanqueducanada.ca", desc: "Collection complète de l'histoire monétaire canadienne" },
          { name: "Wikipédia - Piastre", url: "https://fr.wikipedia.org/wiki/Piastre", desc: "Étymologie et usage historique" },
          { name: "Wikipédia - Pièce de Huit", url: "https://fr.wikipedia.org/wiki/Pi%C3%A8ce_de_huit", desc: "Histoire du dollar espagnol à l'échelle mondiale" },
          { name: "Patrimoine Culturel du Québec", url: "https://www.patrimoine-culturel.gouv.qc.ca", desc: "Découvertes archéologiques de pièces espagnoles au Québec" },
          { name: "Banque du Canada - Monnaie 1600-1770", url: "https://www.bankofcanada.ca/wp-content/uploads/2010/07/1600-1770.pdf", desc: "Documentation officielle de l'histoire monétaire" },
          { name: "Érudit - Histoire Monétaire", url: "https://www.erudit.org", desc: "Articles académiques sur l'histoire financière du Québec" }
        ]
      },
      
      cta: {
        title: "De la Piastre à la Blockchain",
        description: "Tout comme la piastre a révolutionné le commerce en Nouvelle-France, Piastre.one apporte la prochaine évolution de la monnaie au Québec et au-delà.",
        button: "Découvrir Piastre.one"
      }
    },
    // Navbar
    nav: {
      features: "Fonctionnalités",
      howItWorks: "Comment ça marche",
      security: "Sécurité",
      reviews: "Avis",
      login: "Connexion",
      getAccount: "Créer un compte gratuit"
    },
    
    // Hero Section
    hero: {
      badge: "Propulsé par Stellar Blockchain",
      title1: "La banque pour le",
      title2: "monde décentralisé",
      title3: ".",
      description: "Connectez facilement votre compte WordPress à un portefeuille Stellar. Gagnez des cryptos avec vos points MyCred, dépensez partout et prenez le contrôle de votre avenir financier.",
      ctaPrimary: "Ouvrir un compte gratuit",
      ctaSecondary: "Voir comment ça marche",
      trustSecurity: "Sécurité bancaire",
      trustInstant: "Configuration instantanée"
    },
    
    // Social Proof
    socialProof: {
      title: "Approuvé par les plateformes leaders"
    },
    
    // Features Section
    features: {
      title1: "Tout ce dont vous avez besoin,",
      title2: "en un seul endroit",
      description: "De l'accumulation de points aux dépenses en crypto — gérez tout votre parcours financier avec Piastre.one.",
      
      wallet: {
        badge: "Votre Portefeuille",
        title: "Votre portefeuille crypto, créé automatiquement",
        description: "Connectez votre compte WordPress et obtenez un portefeuille Stellar instantanément. Pas de phrase de récupération à gérer — juste un accès simple et sécurisé."
      },
      earn: {
        badge: "Gagner des Points",
        title: "Transformez vos points en crypto",
        description: "Convertissez vos points MyCred en tokens Stellar en un clic."
      },
      spend: {
        badge: "Dépenser",
        title: "Dépensez où que vous soyez",
        description: "Utilisez votre carte Piastre pour les paiements sans contact dans le monde entier."
      },
      send: {
        badge: "Transférer",
        title: "Envoyez et recevez instantanément",
        description: "Transférez des cryptos à vos amis en quelques secondes avec des frais quasi nuls sur le réseau Stellar.",
        youSend: "Vous envoyez",
        theyReceive: "Ils reçoivent"
      }
    },
    
    // How It Works
    howItWorks: {
      overline: "Inscription Simple",
      title: "Commencez en 3 étapes faciles",
      step1: {
        title: "Connectez WordPress",
        description: "Liez votre compte WordPress MyCred en quelques secondes avec notre intégration OAuth sécurisée."
      },
      step2: {
        title: "Vérifiez votre identité",
        description: "Vérification rapide pour sécuriser votre compte. Moins de 2 minutes."
      },
      step3: {
        title: "Commencez à gagner",
        description: "Votre portefeuille est prêt ! Gagnez des tokens, convertissez vos points et dépensez partout."
      }
    },
    
    // Security Section
    security: {
      title: "Votre sécurité est notre priorité",
      description: "Construit avec les mêmes standards de sécurité que les banques traditionnelles, amélioré par la transparence blockchain.",
      feature1: {
        title: "Chiffrement 256 bits",
        description: "Sécurité bancaire"
      },
      feature2: {
        title: "Protection anti-fraude",
        description: "Surveillance 24/7"
      },
      feature3: {
        title: "Stockage sécurisé",
        description: "Sauvegarde cold wallet"
      }
    },
    
    // Stats
    stats: {
      users: "Utilisateurs actifs",
      converted: "Tokens convertis",
      rating: "Note App Store",
      speed: "Vitesse de transaction"
    },
    
    // Testimonials
    testimonials: {
      overline: "Aimé par des milliers",
      title: "Ce que disent nos utilisateurs",
      reviews: [
        {
          name: "Sarah Chen",
          role: "Créatrice WordPress",
          content: "Enfin, un portefeuille crypto qui ne nécessite pas un doctorat pour l'utiliser. J'ai connecté mon compte MyCred en quelques secondes !"
        },
        {
          name: "Marcus Johnson",
          role: "Entrepreneur en ligne",
          content: "La conversion des points en tokens est fluide. Ma communauté adore gagner de vraies récompenses crypto."
        },
        {
          name: "Emily Rodriguez",
          role: "Créatrice de contenu",
          content: "Une sécurité bancaire avec la simplicité de Venmo. C'est comme ça que la crypto aurait dû être dès le départ."
        },
        {
          name: "David Kim",
          role: "Propriétaire PME",
          content: "Intégré à ma boutique WordPress en quelques minutes. Maintenant mes clients gagnent des tokens à chaque achat !"
        }
      ]
    },
    
    // CTA Section
    cta: {
      title: "Prêt à prendre le contrôle de votre crypto ?",
      description: "Rejoignez des milliers d'utilisateurs WordPress qui ont déjà fait le changement vers Piastre.one. C'est gratuit pour commencer.",
      primaryBtn: "Ouvrir un compte gratuit",
      secondaryBtn: "Parler aux ventes"
    },
    
    // Footer
    footer: {
      description: "La banque pour le monde décentralisé. Connectez, gagnez et dépensez — tout depuis votre compte WordPress.",
      product: "Produit",
      company: "Entreprise",
      legal: "Légal",
      features: "Fonctionnalités",
      pricing: "Tarifs",
      security: "Sécurité",
      api: "API",
      about: "À propos",
      blog: "Blog",
      careers: "Carrières",
      contact: "Contact",
      privacy: "Confidentialité",
      terms: "Conditions",
      cookies: "Cookies",
      copyright: "© 2026 Piastre.one. Tous droits réservés.",
      bankGrade: "Sécurité bancaire",
      poweredBy: "Propulsé par Stellar"
    }
  }
};

export default translations;
